import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
// import { get } from "lodash";
import {
  GetReservationList,
  GetPendingReservationList,
  GetConfirmedReservationList,
  UpdateReservation,
  createReservation,
} from "../../components/API/reservationsListAPI";

const getErrorMessage = (error, fallback = "Something went wrong") =>
  error?.message || error?.response?.data?.message || error?.response?.data || fallback;

const normalizeReservationList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.reservations)) return payload.reservations;
  return [];
};

const initialState = {
  reservations: [],
  pendingReservations: [],
  confirmedReservations: [],
  items: [],
  loading: {
    all: false,
    pending: false,
    confirmed: false,
    update: false,
    create: false,
  },
  error: {
    all: null,
    pending: null,
    confirmed: null,
    update: null,
    create: null,
  },
};

export const getReservationList = createAsyncThunk(
  "reservations/getReservationList",
  async (_, { rejectWithValue }) => {
    try {
      const data = await GetReservationList();
      return normalizeReservationList(data);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Unable to load reservation history"));
    }
  }
);

export const getPendingReservationList = createAsyncThunk(
  "reservations/getPendingReservationList",
  async (_, { rejectWithValue }) => {
    try {
      const data = await GetPendingReservationList();
      return normalizeReservationList(data);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Unable to load pending reservations"));
    }
  }
);

export const getConfirmedReservationList = createAsyncThunk(
  "reservations/getConfirmedReservationList",
  async (_, { rejectWithValue }) => {
    try {
      const data = await GetConfirmedReservationList();
      return normalizeReservationList(data);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Unable to load approved reservations"));
    }
  }
);

export const updateReservation = createAsyncThunk(
  "reservations/updateReservation",
  async (
    { id, status, newDate, newTimeFrom, newTimeTo },
    { rejectWithValue }
  ) => {
    try {
      const data = await UpdateReservation(
        id,
        status,
        newDate,
        newTimeFrom,
        newTimeTo
      );
      return data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Unable to update reservation"));
    }
  }
);

export const addReservation = createAsyncThunk(
  "reservations/addReservation",
  async (reservationData, { rejectWithValue }) => {
    try {
      const response = await createReservation(reservationData);
      return response;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Reservation request failed"));
    }
  }
);

export const reservationsSlice = createSlice({
  name: "reservations",
  initialState,
  reducers: {
    updateReservationState: (state, action) => {
      const index = state.reservations.findIndex(
        (res) => res.id === action.payload.id
      );
      if (index !== -1) {
        state.reservations[index] = {
          ...state.reservations[index],
          ...action.payload.reservationData,
        };
      }
    },
    clearReservationErrors: (state) => {
      state.error = initialState.error;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getReservationList.pending, (state) => {
        state.loading.all = true;
        state.error.all = null;
      })
      .addCase(getReservationList.fulfilled, (state, action) => {
        state.reservations = normalizeReservationList(action.payload);
        state.loading.all = false;
      })
      .addCase(getReservationList.rejected, (state, action) => {
        state.error.all = action.payload;
        state.loading.all = false;
      })

      .addCase(getPendingReservationList.pending, (state) => {
        state.loading.pending = true;
        state.error.pending = null;
      })
      .addCase(getPendingReservationList.fulfilled, (state, action) => {
        state.pendingReservations = normalizeReservationList(action.payload);
        state.loading.pending = false;
      })
      .addCase(getPendingReservationList.rejected, (state, action) => {
        state.error.pending = action.payload;
        state.loading.pending = false;
      })

      .addCase(getConfirmedReservationList.pending, (state) => {
        state.loading.confirmed = true;
        state.error.confirmed = null;
      })
      .addCase(getConfirmedReservationList.fulfilled, (state, action) => {
        state.confirmedReservations = normalizeReservationList(action.payload);
        state.loading.confirmed = false;
      })
      .addCase(getConfirmedReservationList.rejected, (state, action) => {
        state.error.confirmed = action.payload;
        state.loading.confirmed = false;
      })

      .addCase(updateReservation.pending, (state) => {
        state.loading.update = true;
        state.error.update = null;
      })
      .addCase(updateReservation.fulfilled, (state, { payload }) => {
        state.loading.update = false;
      })
      .addCase(updateReservation.rejected, (state, action) => {
        state.loading.update = false;
        state.error.update = action.payload;
      })

      .addCase(addReservation.pending, (state) => {
        state.loading.create = true;
        state.error.create = null;
      })
      .addCase(addReservation.fulfilled, (state, action) => {
        state.items.push(action.payload);
        state.loading.create = false;
        state.error.create = null;
      })
      .addCase(addReservation.rejected, (state, action) => {
        state.loading.create = false;
        state.error.create = action.payload;
      });
  },
});

export const { reducer, clearReservationErrors } = reservationsSlice;

export default reservationsSlice.reducer;
